import type { AssetAccount, VaultHistoryProfile, VaultProfile } from '@ducat-unit/core';
import { asset as assetSchema } from '@ducat-unit/core/schema';

import { captureError } from '@/lib/posthog/captureError';
import { wait } from '@/lib/util';

import { bucketSizeByTimeSpan, dayInSec, get, rangeByTimeSpan } from '../api.helpers';
import type { PaginatedResponse, TimeSpan } from '../api.types';
import { WalletAssetsIncompleteError } from '../errors';
import { formatStatsVolume } from '../protocol-stats/format-stats-volume';
import type { VaultTransaction } from '../types';
import type { OverviewMetrics, OverviewVolumeHistory, StatsTxItem } from '../types.validator';

import { convertVaultHistory } from './converters';
import fixtureStatsVolume from './fixtures/validator.stats-volume.json';
import fixtureStatsVolumeHistory from './fixtures/validator.stats-volume-history.json';

const isStatsFixtureOn = false;

export async function fetchVaultHistory(vaultTxId: string, validatorUrl: string): Promise<VaultTransaction[]> {
  // const url = `${VALIDATOR_URL}/api/vault/history/${vaultTxId}`;
  const url = `${validatorUrl}/api/vault/${vaultTxId}/history`;
  const response = await get<PaginatedResponse<VaultHistoryProfile>>(url);
  const vaultTransactions = convertVaultHistory(response.items || response.data || response);
  // console.log(`- formatted vault history txs (${vaultTransactions.length})`, vaultTransactions);

  return vaultTransactions;
}

// Validator-side MAX_PAGE_SIZE; larger values are clamped, never rejected.
const WALLET_ASSETS_PAGE_SIZE = 250;
// The validator filters rune-less/tainted UTXOs AFTER slicing a page out of its
// address index, so pages can come back empty with has_more=true and the number
// of pages is unrelated to the number of assets. The cap only bounds a runaway
// cursor loop (~10k raw UTXO records at page_size 250).
const WALLET_ASSETS_MAX_PAGES = 40;

function rejectWalletInventory(message: string, source: string, pages: number): never {
  const error = new WalletAssetsIncompleteError(message);
  captureError(error, { source, additionalContext: { pages } });
  throw error;
}

export async function fetchWalletAssets(
  assetAddress: string,
  validatorUrl: string,
  options?: { maxIterations?: number },
): Promise<AssetAccount[]> {
  const maxIterations = options?.maxIterations ?? WALLET_ASSETS_MAX_PAGES;
  const url = `${validatorUrl}/api/address/${assetAddress}`;
  const allData: AssetAccount[] = [];

  let cursor: string | undefined;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const response = await get<unknown>(url, {
      page_size: WALLET_ASSETS_PAGE_SIZE,
      ...(cursor ? { cursor } : {}),
    });

    if (!response || typeof response !== 'object' || Array.isArray(response)) {
      rejectWalletInventory('Invalid wallet asset inventory response', 'WALLET_ASSETS_INVALID_RESPONSE', iteration + 1);
    }
    const page = response as Partial<PaginatedResponse<AssetAccount>>;
    const pageData = page.data ?? page.items;
    if (
      typeof page.has_more !== 'boolean' ||
      !Array.isArray(pageData) ||
      pageData.some((row) => !assetSchema.account.safeParse(row).success)
    ) {
      rejectWalletInventory('Invalid wallet asset inventory page', 'WALLET_ASSETS_INVALID_PAGE', iteration + 1);
    }
    allData.push(...pageData);

    const nextCursor = page.next_cursor;
    if (!page.has_more) {
      return allData;
    }

    if (typeof nextCursor !== 'string' || !nextCursor || nextCursor === cursor) {
      rejectWalletInventory('Wallet asset inventory cursor did not advance', 'WALLET_ASSETS_CURSOR_LOOP', iteration + 1);
    }

    cursor = nextCursor;
  }

  // An incomplete asset list understates the UNIT balance and lets rune UTXOs
  // leak into BTC coin selection (useSatsUtxos trusts it to be complete), so
  // fail loudly instead of returning partial data.
  rejectWalletInventory(
    `Wallet asset inventory page cap of ${maxIterations} reached; asset list incomplete`,
    'WALLET_ASSETS_TRUNCATED',
    maxIterations,
  );
}

export { fetchProtoProfile } from './protocol-profile';

// Get aggregated protocol volume statistics.
export async function fetchStatsVolume(time_span: TimeSpan, validatorUrl: string) {
  // const url = `${VALIDATOR_URL}/api/overall_volume`; // v2.5
  const url = `${validatorUrl}/api/stats/volume`; // v3

  if (isStatsFixtureOn) {
    await wait(1000);
    return fixtureStatsVolume;
  }

  const response = await get<OverviewMetrics>(url, { time_span });

  return formatStatsVolume(response);
}

// Get time-bucketed volume history for charting.
export async function fetchStatsVolumeHistory(
  time_span: TimeSpan,
  validatorUrl: string,
  signal?: AbortSignal,
): Promise<OverviewVolumeHistory[]> {
  // const url = `${VALIDATOR_URL}/api/range_volume`; // v2.5
  const url = `${validatorUrl}/api/stats/volume/history`; // v3

  const bucket_size = bucketSizeByTimeSpan[time_span];
  const timestamp_end = Math.floor(Date.now() / 1000);
  // For now (since v3 endpoint has max page_size of 250) fetch 250 for 1 year period.
  const rangeSec = time_span === 'year' ? dayInSec * 250 : rangeByTimeSpan[time_span];
  const timestamp_start = timestamp_end - rangeSec;

  if (isStatsFixtureOn) {
    await wait(1000);
    return fixtureStatsVolumeHistory.data;
  }

  const response = await get<PaginatedResponse<OverviewVolumeHistory>>(
    url,
    {
      bucket_size,
      timestamp_start,
      timestamp_end,
      page_size: 250,
    },
    { signal },
  );

  // We dont use pagination for chart data, so just ignore it.
  return response.data;
}

// Get paginated vault action transactions (newest-first).
export async function fetchStatsTxHistory(validatorUrl: string, cursor?: string, page_size = 50): Promise<PaginatedResponse<StatsTxItem>> {
  // const url = `${VALIDATOR_URL}/api/range_history_all`; // v2.5
  const url = `${validatorUrl}/api/stats/tx`; // v3

  return get<PaginatedResponse<StatsTxItem>>(url, {
    cursor,
    page_size,
  });
}

// Get a paginated list of all vaults as profiles.
export async function fetchAllVaults(
  validatorUrl: string,
  cursor?: string | null,
  page_size = 50,
): Promise<PaginatedResponse<VaultProfile>> {
  // const url = `${VALIDATOR_URL}/api/vault_list`; // v2.5
  const url = `${validatorUrl}/api/vault/all`; // v3

  const response = await get<PaginatedResponse<VaultProfile>>(url, {
    cursor,
    page_size,
  });

  if (response.items && !response.data) {
    response.data = response.items; // normalize typescript validator version response.
  }

  return response;
}

export async function fetchAccountVaultList(validatorUrl: string, vaultPubkey: string) {
  const { fetch_pubkey_vaults } = await import('@ducat-unit/client-sdk/lib');
  const response = await fetch_pubkey_vaults(validatorUrl, vaultPubkey);

  if (response.ok === false) {
    throw Error(response.error);
  }

  return response.data;
}

// function formatVolumeHistory(data: OverviewVolumeHistory[]) {
//   return data.map((item) => {
//     return {
//       ...item,
//       btc_locked: toBtc(item.btc_locked),
//       unit_borrowed: roundNumber(item.unit_borrowed / 100, 2),
//     };
//   });
// }
